/**
 * Voice Chat Component
 * Displays connection status, audio controls, and participant info
 * Wrapped with error boundary to prevent crashes
 */

import { Component, ErrorInfo, ReactNode, useEffect } from 'react'
import { ConnectionStatus, VoiceStatus } from '../hooks/usePeerJS'
import '../styles/VoiceChat.css'

interface VoiceChatProps {
  connectionStatus: ConnectionStatus
  voiceStatus: VoiceStatus
  playerName: string
  opponentName?: string
  onToggleMute: () => void
  onConnect?: () => void
  onDisconnect?: () => void
}

// Error boundary state
interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

// Error boundary wrapper
class VoiceChatErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  constructor(props: { children: ReactNode }) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[VoiceChat Error Boundary] Caught error:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="voice-chat-container voice-chat-error">
          <div className="voice-error-info">
            <p>⚠️ Voice chat unavailable</p>
            <small>Game state will sync via server</small>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

function VoiceChatInner({
  connectionStatus,
  voiceStatus,
  playerName,
  opponentName,
  onToggleMute,
  onConnect,
  onDisconnect,
}: VoiceChatProps) {
  // Detect mobile device
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)

  // Debug logging on mount and status changes
  useEffect(() => {
    console.log('[VoiceChat] Status changed:', {
      connectionStatus,
      hasOnConnect: !!onConnect,
      isMobile,
      playerName
    })
  }, [connectionStatus])

  const getStatusColor = () => {
    switch (connectionStatus) {
      case 'connected':
        return '#10b981' // green
      case 'connecting':
        return '#f59e0b' // orange
      case 'disconnected':
      case 'failed':
        return '#ef4444' // red
      default:
        return '#6b7280' // gray
    }
  }

  const getStatusText = () => {
    switch (connectionStatus) {
      case 'connected':
        return 'Connected'
      case 'connecting':
        return 'Connecting...'
      case 'disconnected':
        return 'Disconnected'
      case 'failed':
        return 'Connection Failed'
      default:
        return 'Not Connected'
    }
  }

  return (
    <div className="voice-chat-container">
      <div className="voice-chat-header">
        <div className="voice-status-indicator" style={{ backgroundColor: getStatusColor() }} />
        <span className="voice-status-text">{getStatusText()}</span>
      </div>

      {connectionStatus === 'connected' && (
        <div className="voice-participants">
          <div className="voice-participant">
            <div className={`mic-icon ${voiceStatus.localMuted ? 'muted' : 'active'}`}>
              {voiceStatus.localMuted ? '🔇' : '🎤'}
            </div>
            <span className="participant-name">{playerName} (You)</span>
          </div>

          {opponentName && (
            <div className="voice-participant">
              <div className={`mic-icon ${voiceStatus.remoteConnected ? 'active' : 'inactive'}`}>
                {voiceStatus.remoteConnected ? '🔊' : '🔇'}
              </div>
              <span className="participant-name">{opponentName}</span>
            </div>
          )}
        </div>
      )}

      <div className="voice-controls">
        <button
          className={`voice-btn ${voiceStatus.localMuted ? 'btn-unmute' : 'btn-mute'}`}
          onClick={onToggleMute}
          disabled={connectionStatus !== 'connected'}
          title={voiceStatus.localMuted ? 'Unmute' : 'Mute'}
        >
          {voiceStatus.localMuted ? '🔇 Unmute' : '🎤 Mute'}
        </button>

        {onDisconnect && connectionStatus === 'connected' && (
          <button
            className="voice-btn btn-disconnect"
            onClick={onDisconnect}
            title="Disconnect voice"
          >
            📞 Hang Up
          </button>
        )}
      </div>

      {connectionStatus === 'connecting' && (
        <div className="voice-connecting-info">
          <div className="spinner" />
          <p>Waiting for other player...</p>
          <small style={{ opacity: 0.7, fontSize: '0.8em' }}>
            Both players must click "Enable Voice Chat" to connect. Game works without it.
          </small>
        </div>
      )}

      {connectionStatus === 'failed' && (
        <div className="voice-error-info">
          <p>Connection timeout - other player may not be ready</p>
          <small style={{ opacity: 0.7, fontSize: '0.8em' }}>
            Try again when both players are online. Game works perfectly without voice.
          </small>
          {onConnect && (
            <button
              className="voice-btn btn-connect"
              onClick={onConnect}
              style={{
                backgroundColor: '#ef4444',
                color: 'white',
                padding: '8px 16px',
                borderRadius: '6px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '0.9em',
                marginTop: '8px',
              }}
            >
              Retry Connection
            </button>
          )}
        </div>
      )}

      {(connectionStatus === 'disconnected' || connectionStatus === 'closed') && (
        <div className="voice-error-info">
          {onConnect ? (
            <>
              <button
                className="voice-btn btn-connect"
                onClick={() => {
                  console.log('[VoiceChat] Button clicked - calling onConnect')
                  console.log('[VoiceChat] onConnect is:', typeof onConnect, onConnect)
                  if (onConnect) {
                    onConnect()
                    console.log('[VoiceChat] onConnect() called successfully')
                  } else {
                    console.error('[VoiceChat] onConnect is not defined!')
                  }
                }}
                style={{
                  backgroundColor: '#10b981',
                  color: 'white',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '1em',
                  fontWeight: 'bold',
                  marginBottom: '8px',
                  width: '100%',
                }}
              >
                🎤 Enable Voice Chat
              </button>
              <small style={{ opacity: 0.7, fontSize: '0.8em', display: 'block', textAlign: 'center' }}>
                {isMobile ? 'Tap to request microphone access' : 'Click to enable voice chat'}
              </small>
            </>
          ) : (
            <>
              <p>Voice chat not connected</p>
              <small style={{ opacity: 0.7, fontSize: '0.8em' }}>
                Game will sync via server
              </small>
            </>
          )}
        </div>
      )}
    </div>
  )
}

// Export with error boundary wrapper
export function VoiceChat(props: VoiceChatProps) {
  return (
    <VoiceChatErrorBoundary>
      <VoiceChatInner {...props} />
    </VoiceChatErrorBoundary>
  )
}
